/**
 * KetoFusionLanding — Branded "Fusion" experience.
 *
 * Visual identity: black canvas + green/purple neon fusion (Keto Lab Method).
 * Tagline: "Teu corpo, a nossa fórmula."
 */

import React, { useEffect, useState, useContext } from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Flame, Check, Lock, Brain, Leaf, Zap, Atom } from "lucide-react";
import { AuthContext } from "../App";
import KetoFusionOnboarding from "@/components/KetoFusionOnboarding";
import PhasePreviewModal from "@/components/progression/PhasePreviewModal";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL;
const FUSION_HERO = "https://customer-assets.emergentagent.com/job_10364079-809d-4ab0-bd35-73b1a2bd39a9/artifacts/z4bcxqbr_IMG-20260613-WA0000.jpg";

const BLOCK_THEME = {
  1: { color: "#84cc16", bg: "from-lime-500/15 via-zinc-950 to-black", glow: "shadow-lime-500/30" },
  2: { color: "#a855f7", bg: "from-purple-500/15 via-zinc-950 to-black", glow: "shadow-purple-500/30" },
  3: { color: "#84cc16", bg: "from-lime-500/15 via-zinc-950 to-black", glow: "shadow-lime-500/30" },
  4: { color: "#a855f7", bg: "from-purple-500/15 via-zinc-950 to-black", glow: "shadow-purple-500/30" },
};

const BENEFITS = [
  { icon: Flame, color: "#84cc16", label: "Energia Estável" },
  { icon: Brain, color: "#a855f7", label: "Foco Mental" },
  { icon: Leaf, color: "#84cc16", label: "Queima Eficiente" },
  { icon: Atom, color: "#a855f7", label: "Ciência Aplicada" },
];

export default function KetoFusionLanding() {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [protocol, setProtocol] = useState(null);
  const [science, setScience] = useState(null);
  const [activeBlock, setActiveBlock] = useState(1);
  const [enrolled, setEnrolled] = useState(null);
  const [phaseState, setPhaseState] = useState(null);
  const [wizardOpen, setWizardOpen] = useState(false);
  const [previewPhase, setPreviewPhase] = useState(null);

  useEffect(() => {
    fetch(`${API}/api/keto-fusion/protocol`).then((r) => r.json()).then(setProtocol).catch(() => {});
    fetch(`${API}/api/keto-fusion/science`).then((r) => r.json()).then(setScience).catch(() => {});
    if (user) {
      fetch(`${API}/api/keto-fusion/today`, { headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } })
        .then((r) => r.json())
        .then((d) => { if (d?.enrolled) setEnrolled(d); })
        .catch(() => {});
      fetch(`${API}/api/programs/keto_fusion/phase`, { headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } })
        .then((r) => r.json())
        .then(setPhaseState)
        .catch(() => {});
    }
  }, [user]);

  const goToProgram = () => {
    if (enrolled) navigate("/keto-fusion/today");
    else if (!user) navigate("/");
    else setWizardOpen(true);
  };

  const ctaLabel = !user ? "Criar conta para começar" : enrolled ? "Continuar Keto Fusion" : "Activar Keto Fusion";

  return (
    <div className="min-h-screen bg-black text-zinc-100 relative overflow-x-hidden">
      {/* Ambient fusion glow */}
      <div className="fixed top-0 left-0 w-1/2 h-screen pointer-events-none opacity-40">
        <div className="absolute top-1/4 left-1/4 w-96 h-96 rounded-full blur-[120px] bg-lime-500/30 animate-pulse" />
      </div>
      <div className="fixed top-0 right-0 w-1/2 h-screen pointer-events-none opacity-40">
        <div className="absolute top-1/2 right-1/4 w-96 h-96 rounded-full blur-[120px] bg-purple-500/30 animate-pulse" style={{ animationDelay: "1.5s" }} />
      </div>

      {/* HERO with brand image */}
      <section className="relative">
        <div className="max-w-7xl mx-auto grid lg:grid-cols-2 gap-0 items-center min-h-[80vh]">
          {/* Left — Brand image with DNA helix (animated breathing + slow rotation) */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1 }}
            className="relative h-[60vh] lg:h-[80vh] overflow-hidden flex items-center justify-center"
          >
            <motion.img
              src={FUSION_HERO}
              alt="Keto Fusion DNA"
              className="absolute inset-0 w-full h-full object-cover"
              style={{ filter: "drop-shadow(0 0 40px rgba(132,204,22,0.4))" }}
              animate={{
                scale: [1, 1.04, 1],
                filter: [
                  "drop-shadow(0 0 30px rgba(132,204,22,0.4)) drop-shadow(0 0 60px rgba(168,85,247,0.2)) hue-rotate(0deg)",
                  "drop-shadow(0 0 50px rgba(132,204,22,0.6)) drop-shadow(0 0 80px rgba(168,85,247,0.4)) hue-rotate(10deg)",
                  "drop-shadow(0 0 30px rgba(132,204,22,0.4)) drop-shadow(0 0 60px rgba(168,85,247,0.2)) hue-rotate(0deg)",
                ],
              }}
              transition={{ duration: 7, repeat: Infinity, ease: "easeInOut" }}
            />

            {/* Rotating energy ring overlay */}
            <motion.div
              className="absolute inset-8 rounded-full pointer-events-none"
              style={{
                background: "conic-gradient(from 0deg, transparent 70%, rgba(132,204,22,0.3) 80%, rgba(168,85,247,0.4) 90%, transparent 100%)",
                filter: "blur(30px)",
              }}
              animate={{ rotate: 360 }}
              transition={{ duration: 18, repeat: Infinity, ease: "linear" }}
            />

            {/* Counter-rotating particle ring */}
            <motion.div
              className="absolute inset-16 rounded-full border border-lime-500/20 pointer-events-none"
              animate={{ rotate: -360 }}
              transition={{ duration: 28, repeat: Infinity, ease: "linear" }}
            >
              {[0, 60, 120, 180, 240, 300].map((deg, i) => (
                <motion.div
                  key={i}
                  className="absolute w-2 h-2 rounded-full"
                  style={{
                    background: i % 2 === 0 ? "#84cc16" : "#a855f7",
                    boxShadow: `0 0 10px ${i % 2 === 0 ? "#84cc16" : "#a855f7"}`,
                    top: "50%",
                    left: "50%",
                    transform: `rotate(${deg}deg) translateY(-180px)`,
                  }}
                  animate={{ opacity: [0.4, 1, 0.4] }}
                  transition={{ duration: 3, repeat: Infinity, delay: i * 0.4 }}
                />
              ))}
            </motion.div>

            <div className="absolute inset-0 bg-gradient-to-r from-transparent via-transparent to-black pointer-events-none" />

            {/* Animated scan line */}
            <motion.div
              className="absolute left-0 right-0 h-px bg-gradient-to-r from-transparent via-lime-400 to-transparent pointer-events-none"
              animate={{ top: ["0%", "100%", "0%"] }}
              transition={{ duration: 6, repeat: Infinity, ease: "linear" }}
            />
          </motion.div>

          {/* Right — Typography */}
          <div className="relative px-6 lg:px-10 py-12 lg:py-0 z-10">
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }}>
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-zinc-900 border border-zinc-800 mb-6">
                <span className="w-1.5 h-1.5 rounded-full bg-lime-400 animate-pulse" />
                <span className="text-[11px] uppercase tracking-widest text-zinc-400">Keto Lab Method · €39/mês</span>
              </div>

              <h1
                className="text-6xl sm:text-7xl lg:text-8xl font-black tracking-tight leading-none mb-2"
                style={{
                  color: "#84cc16",
                  textShadow: "0 0 30px rgba(132,204,22,0.6), 0 0 60px rgba(132,204,22,0.3)",
                  fontFamily: "'Inter', system-ui, sans-serif",
                }}
                data-testid="fusion-title-keto"
              >
                KETO
              </h1>
              <h1
                className="text-6xl sm:text-7xl lg:text-8xl font-black tracking-tight leading-none mb-6"
                style={{
                  color: "#a855f7",
                  textShadow: "0 0 30px rgba(168,85,247,0.6), 0 0 60px rgba(168,85,247,0.3)",
                  fontFamily: "'Inter', system-ui, sans-serif",
                }}
                data-testid="fusion-title-fusion"
              >
                FUSION
              </h1>

              {/* Gradient divider */}
              <div className="h-0.5 w-full max-w-sm bg-gradient-to-r from-lime-500 via-emerald-400 to-purple-500 mb-4 rounded-full" />

              <p className="text-lg italic text-zinc-300 mb-8" data-testid="fusion-tagline">
                Teu corpo, a nossa fórmula.
              </p>

              {/* Benefit icons row */}
              <div className="grid grid-cols-4 gap-4 mb-8 max-w-md">
                {BENEFITS.map((b, i) => {
                  const Icon = b.icon;
                  return (
                    <motion.div
                      key={i}
                      initial={{ opacity: 0, y: 8 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.5 + i * 0.1 }}
                      className="text-center"
                    >
                      <div
                        className="w-12 h-12 mx-auto rounded-xl border flex items-center justify-center mb-2"
                        style={{ borderColor: `${b.color}55`, boxShadow: `0 0 20px ${b.color}25` }}
                      >
                        <Icon className="w-5 h-5" style={{ color: b.color }} />
                      </div>
                      <p className="text-[10px] uppercase tracking-wider text-zinc-400 leading-tight">{b.label}</p>
                    </motion.div>
                  );
                })}
              </div>

              <div className="flex flex-wrap gap-3">
                <Button
                  onClick={goToProgram}
                  className="bg-gradient-to-r from-lime-500 to-purple-500 hover:from-lime-400 hover:to-purple-400 text-white font-semibold text-base px-7 h-12 shadow-lg shadow-purple-500/30"
                  data-testid="fusion-hero-cta"
                >
                  <Flame className="w-5 h-5 mr-2" /> {ctaLabel}
                </Button>
                <Button
                  onClick={() => document.getElementById("roadmap")?.scrollIntoView({ behavior: "smooth" })}
                  variant="outline"
                  className="border-zinc-700 text-zinc-300 hover:bg-zinc-900 hover:text-white h-12"
                >
                  Ver mapa 12 semanas
                </Button>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Roadmap */}
      <section id="roadmap" className="max-w-6xl mx-auto px-6 py-20 relative">
        <h2 className="text-3xl sm:text-4xl font-bold mb-2 text-center">
          <span style={{ color: "#84cc16" }}>4 blocos.</span>{" "}
          <span style={{ color: "#a855f7" }}>12 semanas.</span>{" "}
          <span className="text-zinc-100">84 missões.</span>
        </h2>
        <p className="text-zinc-400 mb-12 text-center">Cada missão escolhida com intenção. Cada bloco evolui o anterior.</p>

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
          {protocol?.blocks &&
            Object.entries(protocol.blocks).map(([num, b]) => {
              const t = BLOCK_THEME[num] || BLOCK_THEME[1];
              const blockId = Number(num);
              const rmap = phaseState?.roadmap?.find((p) => p.id === blockId);
              const status = rmap?.status || (enrolled ? "locked" : null);
              const isLocked = enrolled && status === "locked";
              const isActive = status === "active";
              const isPassed = status === "passed";
              const canOpen = enrolled && !isLocked;

              return (
                <motion.button
                  key={num}
                  initial={{ opacity: 0, y: 16 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: blockId * 0.08 }}
                  whileHover={{ y: -4, transition: { duration: 0.2 } }}
                  onClick={() => {
                    if (canOpen && isActive) {
                      navigate("/programs/keto_fusion/phase");
                    } else if (canOpen && isPassed) {
                      // Show preview for passed phase (rever)
                      setPreviewPhase({ id: blockId, status: "passed" });
                    } else if (isLocked) {
                      // Cinematic preview instead of dry toast
                      setPreviewPhase({ id: blockId, status: "locked" });
                    } else {
                      // Not enrolled — scroll to CTA
                      document.getElementById("pricing")?.scrollIntoView({ behavior: "smooth" });
                    }
                  }}
                  className={`p-5 rounded-2xl bg-gradient-to-br ${t.bg} border shadow-2xl ${t.glow} text-left transition-all relative overflow-hidden ${canOpen ? "cursor-pointer hover:scale-[1.02]" : "cursor-default"}`}
                  style={{
                    borderColor: isActive ? t.color : `${t.color}40`,
                    boxShadow: isActive ? `0 0 30px ${t.color}40` : undefined,
                    opacity: isLocked ? 0.65 : 1,
                  }}
                  data-testid={`fusion-block-${num}`}
                >
                  {/* Active pulse halo */}
                  {isActive && (
                    <motion.div
                      className="absolute inset-0 pointer-events-none"
                      style={{ background: `radial-gradient(circle at 30% 30%, ${t.color}20, transparent 70%)` }}
                      animate={{ opacity: [0.4, 0.8, 0.4] }}
                      transition={{ duration: 3, repeat: Infinity }}
                    />
                  )}

                  {/* Status badge */}
                  {status && (
                    <div className="absolute top-3 right-3 flex items-center gap-1 z-10">
                      {isPassed && <Check className="w-3.5 h-3.5 text-emerald-400" />}
                      {isActive && (
                        <span className="text-[9px] uppercase tracking-widest font-bold px-1.5 py-0.5 rounded" style={{ background: `${t.color}25`, color: t.color }}>
                          Activo
                        </span>
                      )}
                      {isLocked && <Lock className="w-3.5 h-3.5 text-zinc-500" />}
                    </div>
                  )}

                  <p className="text-[10px] uppercase tracking-widest mb-1 relative z-10" style={{ color: t.color }}>
                    Bloco {num}
                  </p>
                  <h3 className="text-xl font-bold mb-1 text-zinc-100 relative z-10">
                    {b.name_pt?.replace(/^Bloco \d+ —\s*/, "")}
                  </h3>
                  <p className="text-xs text-zinc-400 mb-3 relative z-10">{b.subtitle_pt}</p>
                  <p className="text-xs text-zinc-300 leading-relaxed relative z-10">{b.objective_pt}</p>

                  {canOpen && (
                    <p className="text-[10px] uppercase tracking-widest mt-3 relative z-10" style={{ color: t.color }}>
                      {isActive ? "→ Entrar na fase" : "→ Rever fase"}
                    </p>
                  )}
                </motion.button>
              );
            })}
        </div>
      </section>

      {/* Science per Phase — programmatic content with scientific basis */}
      {science?.phases && (
        <section id="science" className="max-w-6xl mx-auto px-6 py-20 relative">
          <div className="text-center mb-10">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-zinc-900 border border-zinc-800 mb-4">
              <Atom className="w-3 h-3 text-lime-400" />
              <span className="text-[10px] uppercase tracking-widest text-zinc-400">Conteúdo Programático</span>
            </div>
            <h2 className="text-3xl sm:text-4xl font-bold mb-2 text-zinc-100">
              A ciência por trás de cada fase
            </h2>
            <p className="text-zinc-400 max-w-2xl mx-auto text-sm">
              Cada bloco é desenhado sobre mecanismos fisiológicos validados.
              Sabes <em>porque</em> fazes o que fazes. Spirit ajusta-se ao bloco activo.
            </p>
          </div>

          {/* Phase tabs */}
          <div className="flex flex-wrap justify-center gap-2 mb-8" data-testid="fusion-science-tabs">
            {science.phases.map((p) => {
              const isActive = activeBlock === p.block;
              return (
                <button
                  key={p.block}
                  onClick={() => setActiveBlock(p.block)}
                  className="px-4 py-2 rounded-full border text-xs font-semibold uppercase tracking-widest transition-all"
                  style={{
                    borderColor: isActive ? p.color : "#27272a",
                    background: isActive ? `${p.color}20` : "transparent",
                    color: isActive ? p.color : "#a1a1aa",
                    boxShadow: isActive ? `0 0 20px ${p.color}40` : "none",
                  }}
                  data-testid={`fusion-science-tab-${p.block}`}
                >
                  Bloco {p.block} · {p.code.replace("_", " ")}
                </button>
              );
            })}
          </div>

          {/* Active phase content */}
          {(() => {
            const phase = science.phases.find((p) => p.block === activeBlock) || science.phases[0];
            return (
              <motion.div
                key={phase.block}
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4 }}
                className="rounded-3xl border bg-zinc-950 p-6 lg:p-10"
                style={{ borderColor: `${phase.color}30`, boxShadow: `0 12px 40px ${phase.color}15` }}
                data-testid={`fusion-science-content-${phase.block}`}
              >
                <div className="flex flex-wrap items-baseline justify-between gap-3 mb-6">
                  <div>
                    <p className="text-[10px] uppercase tracking-widest mb-1" style={{ color: phase.color }}>
                      Semanas {phase.weeks.join("–")} · {phase.duration_days} dias
                    </p>
                    <h3 className="text-2xl sm:text-3xl font-bold text-zinc-100">{phase.name_pt}</h3>
                    <p className="text-sm italic text-zinc-400 mt-1">{phase.tagline_pt}</p>
                  </div>
                </div>

                {/* Objective */}
                <div className="mb-6 p-4 rounded-xl bg-zinc-900/50 border border-zinc-800">
                  <p className="text-[10px] uppercase tracking-widest text-zinc-500 mb-1">Objectivo Fisiológico</p>
                  <p className="text-sm text-zinc-200 leading-relaxed">{phase.objective_pt}</p>
                </div>

                {/* Mechanisms */}
                <p className="text-[10px] uppercase tracking-widest text-zinc-500 mb-3">Mecanismos Fisiológicos</p>
                <div className="grid md:grid-cols-2 gap-3 mb-6">
                  {phase.mechanisms.map((m, i) => (
                    <div
                      key={i}
                      className="p-4 rounded-xl border border-zinc-800 bg-zinc-900/30"
                      data-testid={`fusion-science-mech-${phase.block}-${i}`}
                    >
                      <h4 className="text-sm font-semibold mb-1.5" style={{ color: phase.color }}>
                        {m.title_pt}
                      </h4>
                      <p className="text-xs text-zinc-300 leading-relaxed mb-2">{m.body_pt}</p>
                      <p className="text-[10px] text-zinc-500 italic">📖 {m.reference}</p>
                    </div>
                  ))}
                </div>

                {/* Training & Fueling principles */}
                <div className="grid md:grid-cols-2 gap-3 mb-6">
                  <div className="p-4 rounded-xl border border-zinc-800 bg-zinc-900/30">
                    <p className="text-[10px] uppercase tracking-widest text-lime-400 mb-1.5">⚡ Princípio de Treino</p>
                    <p className="text-xs text-zinc-300 leading-relaxed">{phase.training_principle_pt}</p>
                  </div>
                  <div className="p-4 rounded-xl border border-zinc-800 bg-zinc-900/30">
                    <p className="text-[10px] uppercase tracking-widest text-purple-400 mb-1.5">🍴 Princípio Nutricional</p>
                    <p className="text-xs text-zinc-300 leading-relaxed">{phase.fueling_principle_pt}</p>
                  </div>
                </div>

                {/* Gender adjustments */}
                <div className="grid md:grid-cols-2 gap-3 mb-6">
                  <div className="p-4 rounded-xl border border-pink-500/20 bg-pink-500/5">
                    <p className="text-[10px] uppercase tracking-widest text-pink-400 mb-1.5">♀ Ajuste Feminino</p>
                    <p className="text-xs text-zinc-300 leading-relaxed">{phase.female_adjustment_pt}</p>
                  </div>
                  <div className="p-4 rounded-xl border border-blue-500/20 bg-blue-500/5">
                    <p className="text-[10px] uppercase tracking-widest text-blue-400 mb-1.5">♂ Ajuste Masculino</p>
                    <p className="text-xs text-zinc-300 leading-relaxed">{phase.male_adjustment_pt}</p>
                  </div>
                </div>

                {/* Outcomes + KPIs */}
                <div className="grid md:grid-cols-2 gap-3">
                  <div className="p-4 rounded-xl border border-zinc-800 bg-zinc-900/30">
                    <p className="text-[10px] uppercase tracking-widest text-zinc-500 mb-2">Resultados Esperados</p>
                    <ul className="space-y-1.5">
                      {phase.expected_outcomes_pt.map((o, i) => (
                        <li key={i} className="flex items-start gap-2 text-xs text-zinc-200">
                          <Check className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" style={{ color: phase.color }} />
                          <span>{o}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div className="p-4 rounded-xl border border-zinc-800 bg-zinc-900/30">
                    <p className="text-[10px] uppercase tracking-widest text-zinc-500 mb-2">KPIs do Bloco</p>
                    <ul className="space-y-1.5">
                      {phase.kpis.map((k, i) => (
                        <li key={i} className="flex justify-between text-xs">
                          <span className="text-zinc-400">{k.label_pt}</span>
                          <span className="font-semibold" style={{ color: phase.color }}>{k.target_pt}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </motion.div>
            );
          })()}
        </section>
      )}

      {/* Pricing */}
      <section className="max-w-3xl mx-auto px-6 py-14">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="relative rounded-3xl bg-zinc-950 border border-zinc-800 p-8 text-center overflow-hidden"
          data-testid="fusion-pricing-card"
        >
          {/* Animated border glow */}
          <motion.div
            className="absolute inset-0 rounded-3xl opacity-50 pointer-events-none"
            style={{
              background: "linear-gradient(45deg, transparent 30%, #84cc16, transparent 50%, #a855f7, transparent 70%)",
              backgroundSize: "200% 200%",
            }}
            animate={{ backgroundPosition: ["0% 0%", "100% 100%", "0% 0%"] }}
            transition={{ duration: 8, repeat: Infinity }}
          />
          <div className="relative bg-zinc-950 rounded-3xl p-2">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-zinc-900 border border-zinc-800 mb-5">
              <Zap className="w-3 h-3 text-lime-400" />
              <span className="text-[10px] uppercase tracking-widest text-zinc-400">Programa Avançado</span>
            </div>
            <h3 className="text-5xl font-black mb-2">
              <span style={{ color: "#84cc16", textShadow: "0 0 20px rgba(132,204,22,0.5)" }}>€39</span>
              <span className="text-lg text-zinc-500 font-normal"> / mês</span>
            </h3>
            <p className="text-sm text-zinc-400 mb-6">Acesso completo 12 semanas + Spirit awareness adaptativo.</p>
            <ul className="text-left max-w-md mx-auto space-y-2 mb-6">
              {[
                "84 missões diárias com fundamentação científica",
                "Adaptação por género (♀ cycle sync · ♂ testosterona)",
                "Carb cycling estratégico + força composta progressiva",
                "Spirit IA actualizado ao bloco actual",
                "Cerimónia final + transformation card",
              ].map((line, i) => (
                <li key={i} className="flex items-start gap-2 text-sm text-zinc-300">
                  <Check className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: i % 2 === 0 ? "#84cc16" : "#a855f7" }} />
                  <span>{line}</span>
                </li>
              ))}
            </ul>
            <Button
              onClick={goToProgram}
              className="bg-gradient-to-r from-lime-500 to-purple-500 hover:from-lime-400 hover:to-purple-400 text-white font-semibold text-base px-8 h-12 shadow-lg shadow-purple-500/30"
              data-testid="fusion-pricing-cta"
            >
              {!user && <Lock className="w-4 h-4 mr-2" />}
              {ctaLabel}
            </Button>
            {!user && <p className="text-xs text-zinc-500 mt-3">Acessível para subscritores Unlimited e Keto Fusion.</p>}
          </div>
        </motion.div>
      </section>

      <KetoFusionOnboarding
        open={wizardOpen}
        onClose={() => setWizardOpen(false)}
        onEnrolled={(enr) => {
          setEnrolled(enr);
          setWizardOpen(false);
          setTimeout(() => navigate("/keto-fusion/today"), 400);
        }}
      />

      {/* Phase Preview Modal — cinematic reveal for locked/passed phases */}
      <PhasePreviewModal
        open={!!previewPhase}
        phaseId={previewPhase?.id}
        status={previewPhase?.status || "locked"}
        onClose={() => setPreviewPhase(null)}
      />
    </div>
  );
}
